import type { LandingAttribution } from "./attribution";

export type LeadResultStatus = "created" | "already_joined" | "invalid" | "error";

export interface LeadResult {
  status: LeadResultStatus;
  message: string;
}

interface LeadInput {
  email: string;
  attribution: LandingAttribution;
}

interface ResponseLike {
  ok: boolean;
  json(): Promise<unknown>;
}

interface SubmitDependencies {
  endpoint: string;
  fetch(url: string, init: RequestInit): Promise<ResponseLike>;
  track(name: string, params?: Record<string, unknown>): void;
  trackCreatedConversion(): void;
}

function safely(run: () => void): void {
  try {
    run();
  } catch {
    // Tracking must never affect the waitlist request or its result.
  }
}

function validEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) && email.length <= 254;
}

function trimAttribution(attribution: LandingAttribution): LandingAttribution {
  return Object.fromEntries(
    Object.entries(attribution)
      .map(([key, value]) => [key, value?.trim()])
      .filter((entry): entry is [string, string] => Boolean(entry[1])),
  );
}

export async function submitLead(input: LeadInput, dependencies: SubmitDependencies): Promise<LeadResult> {
  const email = input.email.trim();
  if (!validEmail(email)) {
    safely(() => dependencies.track("lead_error", { reason: "invalid_email" }));
    return { status: "invalid", message: "Enter a valid email address." };
  }

  safely(() => dependencies.track("lead_submit"));
  try {
    const response = await dependencies.fetch(dependencies.endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, ...trimAttribution(input.attribution) }),
    });
    const body = (await response.json().catch(() => ({}))) as {
      status?: string;
      error?: { message?: string };
    };

    if (!response.ok) {
      safely(() => dependencies.track("lead_error", { reason: "api_error" }));
      return {
        status: "error",
        message: body.error?.message || "Could not join the waitlist right now. Please try again.",
      };
    }

    if (body.status === "already_joined") {
      safely(() => dependencies.track("lead_already_joined"));
      return { status: "already_joined", message: "You are already on the Vunora waitlist." };
    }

    if (body.status !== "created") {
      safely(() => dependencies.track("lead_error", { reason: "invalid_response" }));
      return { status: "error", message: "Could not confirm your waitlist signup. Please try again." };
    }

    safely(() => dependencies.track("lead_created"));
    safely(() => dependencies.trackCreatedConversion());
    return {
      status: "created",
      message: "You are on the Vunora waitlist. Your early invite will arrive by email.",
    };
  } catch {
    safely(() => dependencies.track("lead_error", { reason: "network_error" }));
    return { status: "error", message: "Could not join the waitlist right now. Please try again." };
  }
}
